/**
 * 주간/월간 성과보고서 파일 다운로드 (PDF / HWPX)
 *
 * GET /api/task/period-report 는 파일(application/pdf, application/hwp+zip)을 반환하므로
 * 생성된 fetch 기반 클라이언트(JSON 파싱) 대신, OpenAPI 설정(BASE/TOKEN/동적 헤더)을
 * 재사용해 직접 fetch → blob → 다운로드한다. 파일명은 Content-Disposition(RFC5987) 우선.
 */
import { OpenAPI } from '@/api/generated/core/OpenAPI'

export type ReportFormat = 'pdf' | 'hwpx'
export type ReportPeriodParam = 'weekly' | 'monthly'
export type ReportTabParam = 'all' | 'period' | 'department' | 'business'

export interface PeriodReportDownloadParams {
  workspaceId: string
  period: ReportPeriodParam
  tab: ReportTabParam
  format: ReportFormat
  /** 조회 범위 (YYYY-MM-DD). 스냅샷 시점은 X-As-Of 헤더로 별도 전달됨 */
  startDate?: string
  endDate?: string
  departmentId?: string
  businessId?: string
  status?: string
  search?: string
}

/** OpenAPI Resolver(값 또는 함수)를 실제 값으로 해석 */
const resolveValue = async <T>(value: unknown, arg: unknown = {}): Promise<T | undefined> => {
  if (typeof value === 'function') {
    return await (value as (a: unknown) => T | Promise<T>)(arg)
  }
  return value as T | undefined
}

/** Content-Disposition 에서 파일명 추출 (RFC5987 filename* 우선) */
const parseFilename = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) return null
  // filename*=UTF-8''%ED%95%9C... (RFC5987)
  const star = contentDisposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i)
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/["']/g, '').trim())
    } catch {
      /* fallthrough */
    }
  }
  // filename="..."
  const normal = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i)
  if (normal?.[1]) return normal[1].trim()
  return null
}

const defaultFilename = (params: PeriodReportDownloadParams): string => {
  const periodLabel = params.period === 'weekly' ? '주간' : '월간'
  const ext = params.format === 'hwpx' ? 'hwpx' : 'pdf'
  return `${periodLabel}_성과보고서.${ext}`
}

const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function downloadPeriodReport(params: PeriodReportDownloadParams): Promise<void> {
  const base = (OpenAPI.BASE || '').replace(/\/+$/, '')

  const query = new URLSearchParams()
  query.set('workspace_id', params.workspaceId)
  query.set('period', params.period)
  query.set('tab', params.tab)
  query.set('format', params.format)
  if (params.startDate) query.set('start_date', params.startDate)
  if (params.endDate) query.set('end_date', params.endDate)
  if (params.departmentId) query.set('department_id', params.departmentId)
  if (params.businessId) query.set('business_id', params.businessId)
  if (params.status) query.set('status', params.status)
  if (params.search) query.set('search', params.search)
  query.set('download', 'true')

  const url = `${base}/api/task/period-report?${query.toString()}`

  // OpenAPI 동적 헤더(DOT-*) + Authorization 재사용
  const headers: Record<string, string> = {}
  const dynamicHeaders = await resolveValue<Record<string, string>>(OpenAPI.HEADERS)
  if (dynamicHeaders) Object.assign(headers, dynamicHeaders)
  const token = await resolveValue<string>(OpenAPI.TOKEN)
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetch(url, {
    method: 'GET',
    headers,
    credentials: (OpenAPI.CREDENTIALS as RequestCredentials) || 'include',
  })

  if (!response.ok) {
    const error = new Error(`보고서 다운로드에 실패했습니다. (${response.status})`) as Error & { status?: number }
    error.status = response.status
    throw error
  }

  const blob = await response.blob()
  const filename = parseFilename(response.headers.get('Content-Disposition')) || defaultFilename(params)
  triggerBlobDownload(blob, filename)
}
